import React, { useState, useEffect } from 'react';
import { 
  Snackbar, 
  Alert, 
  Menu,
  MenuItem,
  Divider,
  Box,
  Typography,
  Backdrop,
  CircularProgress,
  Select,
  InputLabel,
  IconButton
} from '@mui/material';
import { 
  getNewLeadData, 
  getQualifiedLeadData, 
  getOtherLeadData,   updateLead // <-- Add this line

} from '../../Action/api'; // <-- Import all three APIs
import ViewDetails from './ViewDetails';
import Board from './components/Board/Board'; // Import the new Board component
import RefreshIcon from '@mui/icons-material/Refresh';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

export const SalesBoard = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [viewDetailsOpen, setViewDetailsOpen] = useState(false);
  const [selectedLeadForView, setSelectedLeadForView] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Add this state for month selection (e.g., default to current month)
  const monthOptions = Array.from({ length: 3 }).map((_, i) => {
    const date = dayjs().subtract(i, 'month');
    return {
      label: date.format('MMMM YYYY'),
      value: date.format('MMM-YYYY'),
    };
  });
  const [month, setMonth] = useState(monthOptions[0].value);

  const location = useLocation();

  const leadColumns = [
    { 
      id: 'New', 
      title: 'New Leads', 
      color: '#4F46E5',
      bgColor: '#F0F4FF',
      borderColor: '#C7D2FE'
    },
    { 
      id: 'Qualified', 
      title: 'Qualified', 
      color: '#059669',
      bgColor: '#ECFDF5',
      borderColor: '#A7F3D0'
    },
    { 
      id: 'Disqualified', 
      title: 'Disqualified', 
      color: '#DC2626',
      bgColor: '#FEF2F2',
      borderColor: '#FCA5A5'
    },
    { 
      id: 'Closed - Won', 
      title: 'Closed Won', 
      color: '#16A34A',
      bgColor: '#F0FDF4',
      borderColor: '#BBF7D0'
    },
    { 
      id: 'Closed - Loss', 
      title: 'Closed Loss', 
      color: '#B91C1C',
      bgColor: '#FEF2F2',
      borderColor: '#FECACA'
    }
  ];

  // // This will run when the component mounts (first render)
  // useEffect(() => {
  //   fetchLeadsData();
  //   // eslint-disable-next-line
  // }, []);

  // // This will run when the month changes
  // useEffect(() => {
  //   fetchLeadsData();
  //   // eslint-disable-next-line
  // }, [month]);

  // This will run when the location or month changes
  useEffect(() => {
    fetchLeadsData();
    // eslint-disable-next-line
  }, [location.pathname, month]);

  // Update fetchLeadsData to call the correct API based on column
  const fetchLeadsData = async () => {
    try {
      setLoading(true);
      let allLeads = [];

      // Fetch New Leads
      const newRes = await getNewLeadData(month);
      if (newRes.status && newRes.data) {
        allLeads = [
          ...allLeads,
          ...newRes.data.map(lead => ({ ...lead, leadType: 'New' }))
        ];
      }

      // Fetch Qualified Leads
      const qualifiedRes = await getQualifiedLeadData(month);
      if (qualifiedRes.status && qualifiedRes.data) {
        allLeads = [
          ...allLeads,
          ...qualifiedRes.data.map(lead => ({ ...lead, leadType: 'Qualified' }))
        ];
      }

      // Fetch Other Leads (Disqualified, Closed - Won, Closed - Loss)
      const otherRes = await getOtherLeadData(month);
      if (otherRes.status && otherRes.data) {
        allLeads = [
          ...allLeads,
          ...otherRes.data // keep their leadType from API response!
        ];
      }

      setLeads(allLeads);
      setError(null);
    } catch (err) {
      setError('Error fetching leads: ' + err.message);
      console.error('Error fetching leads:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (lead) => {
    setSelectedLeadForView(lead);
    setViewDetailsOpen(true);
  };

  const handleCloseViewDetails = () => {
    setViewDetailsOpen(false);
    setSelectedLeadForView(null);
  };

  const handleUpdateLead = (leadId, newLeadType) => {
    setLeads(prevLeads => 
      prevLeads.map(lead => 
        lead.leadId === leadId 
          ? { ...lead, leadType: newLeadType, updatedAt: new Date().toISOString() }
          : lead
      )
    );
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      setSelectedLead(null);
    }, 100);
  };

  const handleMoveToType = async (leadType) => {
    if (selectedLead) {
      handleMenuClose();
      
      try {
        setUpdating(true);
        const updatedLeadData = {
          ...selectedLead,
          leadType: leadType,
          updatedAt: new Date().toISOString(),
        };

        const response = await updateLead(updatedLeadData);
        
        if (response.status) {
          handleUpdateLead(selectedLead.leadId, leadType);
          setSnackbar({
            open: true,
            message: `Lead successfully moved to ${leadType}`,
            severity: 'success'
          });
        } else {
          throw new Error(response.message || 'Failed to update lead');
        }
      } catch (error) {
        console.error('Error updating lead:', error);
        setSnackbar({
          open: true,
          message: `Failed to update lead: ${error.message}`,
          severity: 'error'
        });
      } finally {
        setUpdating(false);
      }
    }
  };

  return (
    <>
      {/* Month Selector */}
     

      {/* Use the new Board component */}
      <Board
        leads={leads}
        loading={loading}
        error={error}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterType={filterType}
        setFilterType={setFilterType}
        onRefresh={fetchLeadsData}
        onViewDetails={handleViewDetails}
        onUpdateLead={handleUpdateLead}
        setSnackbar={setSnackbar}
        month={month} // <-- Pass month prop
      />

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: { 
            width: 200, 
            mt: 1,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 2
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onExited={() => {
          setSelectedLead(null);
          setAnchorEl(null);
        }}
      >
        <MenuItem 
          onClick={() => handleViewDetails(selectedLead)}
          sx={{ 
            py: 1.5,
            '&:hover': { bgcolor: '#f8fafc' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
              View Details
            </Typography>
          </Box>
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />
        {leadColumns.filter(c => c.id !== selectedLead?.leadType).map((column) => (
          <MenuItem 
            key={column.id} 
            onClick={() => handleMoveToType(column.id)}
            sx={{ 
              py: 1.5,
              '&:hover': { bgcolor: '#f8fafc' }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ 
                width: 6, 
                height: 6, 
                borderRadius: '50%', 
                bgcolor: column.color 
              }} />
              <Typography variant="body2" sx={{ fontWeight: 600, color: column.color }}>
                Move to {column.title}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>

      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={updating}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <CircularProgress color="inherit" />
          <Typography sx={{ mt: 2 }}>Updating lead...</Typography>
        </Box>
      </Backdrop>

      {/* View Details Dialog */}
      <ViewDetails
        open={viewDetailsOpen}
        onClose={handleCloseViewDetails}
        lead={selectedLeadForView}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SalesBoard;
